import React, { useEffect, useState } from 'react';
import { t } from '@lingui/macro';
import { graphql, useFragment } from 'utils/graphClient';
import { notEmpty } from 'utils/NotEmptyFilter';
import { GroupBase, OptionBase, Select } from 'chakra-react-select';
import { VaultManagementGroupSelect_vaultManager$key } from './__generated__/VaultManagementGroupSelect_vaultManager.graphql';
import { VaultManagementGroupLabel } from '../VaultManagementGroupLabel';

const vaultManagerFragment = graphql`
  fragment VaultManagementGroupSelect_vaultManager on VaultManager {
    id
    vaultManagementGroups(managedByMyself: true) {
      edges {
        node {
          id
          name
          ...VaultManagementGroupLabel_vaultManagementGroup
        }
      }
    }
  }
`;

export interface VaultManagementGroupOption extends OptionBase {
  label: React.ReactNode;
  id: string;
  name: string;
}

interface VaultManagementGroupSelectProps {
  defaultVaultManagementGroupOption?: VaultManagementGroupOption;
  vaultManager: VaultManagementGroupSelect_vaultManager$key;
  onSelect: (value: VaultManagementGroupOption | null) => void;
  isDisabled?: boolean;
  placeholder?: string;
}

export const VaultManagementGroupSelect = ({
  defaultVaultManagementGroupOption,
  vaultManager,
  onSelect,
  isDisabled,
  placeholder,
}: VaultManagementGroupSelectProps) => {
  const { vaultManagementGroups: _vaultManagementGroups } = useFragment(
    vaultManagerFragment,
    vaultManager,
  );

  const vaultManagementGroups = _vaultManagementGroups.edges
    .map(({ node }) => node)
    .filter(notEmpty);

  const initialVaultManagementGroup =
    vaultManagementGroups.length === 1
      ? {
          label: (
            <VaultManagementGroupLabel
              vaultManagementGroup={vaultManagementGroups[0]}
              pl={1}
            />
          ),
          id: vaultManagementGroups[0].id,
          name: vaultManagementGroups[0].name,
        }
      : null;

  const [vaultManagementGroupOption, setVaultManagementGroupOption] =
    useState<VaultManagementGroupOption | null>(
      defaultVaultManagementGroupOption || initialVaultManagementGroup,
    );

  useEffect(() => {
    onSelect(vaultManagementGroupOption);
  }, [vaultManagementGroupOption]);

  return (
    <Select<
      VaultManagementGroupOption,
      false,
      GroupBase<VaultManagementGroupOption>
    >
      placeholder={placeholder || t`Search Vault Management Groups`}
      value={vaultManagementGroupOption}
      onChange={setVaultManagementGroupOption}
      options={vaultManagementGroups.map((vaultManagementGroup) => ({
        label: (
          <VaultManagementGroupLabel
            vaultManagementGroup={vaultManagementGroup}
            pl={1}
          />
        ),
        id: vaultManagementGroup.id,
        name: vaultManagementGroup.name,
      }))}
      isOptionSelected={(option, selectValue) =>
        option.id === selectValue[0]?.id
      }
      filterOption={({ data }, inputValue) =>
        data.name.toLowerCase().includes(inputValue.toLowerCase())
      }
      selectedOptionStyle="check"
      isClearable
      isDisabled={isDisabled}
    />
  );
};
